import React, { useState, useEffect } from 'react';
import { X, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const JobApplicationPopup = ({ isOpen, onClose, position }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    resetForm();
  }, [position]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
      resetForm();
    }, 300);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'f8077285-19e5-4f27-86eb-f3daeae1d2a1',
          subject: `Job Application: ${position?.title || 'General Application'}`,
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          position: position?.title || 'General Application',
          message: formData.message.trim(),
          from_name: formData.name.trim()
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        // Reset form but don't close popup
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        
        // Remove auto-close timeout
        // setTimeout(() => {
        //   setSubmitStatus(null);
        //   handleClose();
        // }, 3000);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Application submission error:', error);
      setSubmitStatus('error');
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-end" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm ${isClosing ? 'animate-[fadeOut_0.3s_ease-out]' : 'animate-[fadeIn_0.3s_ease-out]'}`}></div>

      <div
        className={`relative bg-white rounded-l-xl px-4 sm:px-8 py-8 md:px-12 max-w-xl w-full h-full overflow-y-auto shadow-2xl ${isClosing ? 'animate-[slideOutRight_0.3s_ease-out]' : 'animate-[slideInRight_0.4s_ease-out]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
          @keyframes slideOutRight { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        `}</style>

        <button
          onClick={handleClose}
          className="absolute top-6 right-6 text-[var(--primarycolor)] hover:text-red-800 transition-colors"
          aria-label="Close"
        >
          <X className="w-8 h-8" />
        </button>

        <div className="mb-8">
          <h3 className="text-3xl text-[var(--primarycolor)] font-medium mb-2">
            {position ? `Apply for ${position.title}` : 'Submit Application'}
          </h3>
          <p className="text-gray-600 font-light">Fill in your details and we'll get back to you</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primarycolor)] focus:border-transparent transition-all disabled:bg-gray-50"
              placeholder="Enter your full name"
            />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primarycolor)] focus:border-transparent transition-all disabled:bg-gray-50"
                placeholder="Enter your email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primarycolor)] focus:border-transparent transition-all disabled:bg-gray-50"
                placeholder="Enter your phone number"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primarycolor)] focus:border-transparent transition-all resize-none disabled:bg-gray-50"
              placeholder="Tell us why you're interested in this position..."
            ></textarea>
          </div>

          {/* Status Messages - Below Submit Button */}
          {submitStatus === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="text-green-800">
                <p className="font-medium">Application submitted successfully!</p>
                {/* <p className="text-sm">We'll review your application and get back to you soon.</p> */}
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="text-red-800">
                <p className="font-medium">Failed to submit application</p>
                <p className="text-sm">Please fill all required fields correctly and try again.</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[var(--primarycolor)] hover:bg-red-700 disabled:bg-gray-400 text-white py-3.5 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed mt-6"
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </button>

        </form>
      </div>
    </div>
  );
};

export default JobApplicationPopup;